import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import Blog from '../components/Blog';
import heroBg from '../assets/images/yagoo_hero_bg.jpg';
import cloverImg from '../assets/images/yagoo_care_1.png';
import care2 from '../assets/images/yagoo_care_2.png';
import care3 from '../assets/images/yagoo_care_3.png';
import care4 from '../assets/images/yagoo_care_4.png';
import yagooAvatar from '../assets/images/yagoo_avatar.png';

const blobPositions: React.CSSProperties[] = [
  { top: '20%', left: '10%' },
  { top: '25%', right: '10%' },
  { bottom: '10%', left: '14%' },
  { bottom: '22%', right: '14%' },
];

const CloverBlob: React.FC = () => {
  const offset = 18;
  const r = 30;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.4 }}
      style={{
        position: 'absolute',
        width: 'clamp(110px, 14vw, 170px)',
        height: 'clamp(110px, 14vw, 170px)',
        ...blobPositions[0],
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%">
        <defs>
          <clipPath id="clover-clip">
            <circle cx="50" cy={50 - offset} r={r} />
            <circle cx={50 + offset} cy="50" r={r} />
            <circle cx="50" cy={50 + offset} r={r} />
            <circle cx={50 - offset} cy="50" r={r} />
          </clipPath>
          <clipPath id="clover-clip-inner">
            <circle cx="50" cy={50 - offset} r={r * 0.88} />
            <circle cx={50 + offset} cy="50" r={r * 0.88} />
            <circle cx="50" cy={50 + offset} r={r * 0.88} />
            <circle cx={50 - offset} cy="50" r={r * 0.88} />
          </clipPath>
        </defs>
        <g clipPath="url(#clover-clip)">
          <rect x="0" y="0" width="100" height="100" fill="rgba(255,255,255,0.3)" />
        </g>
        <image
          href={cloverImg}
          x="5" y="5" width="90" height="90"
          clipPath="url(#clover-clip-inner)"
          preserveAspectRatio="xMidYMid slice"
        />
      </svg>
    </motion.div>
  );
};

const FlowerBlob: React.FC = () => {
  const pw = 50;
  const ph = 90;
  const cx = 100;
  const cy = 100;
  const rr = 18;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.55 }}
      style={{
        position: 'absolute',
        width: 'clamp(110px, 14vw, 175px)',
        height: 'clamp(110px, 14vw, 175px)',
        ...blobPositions[1],
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <svg viewBox="0 0 200 200" width="100%" height="100%">
        <defs>
          <clipPath id="flower-clip">
            <rect x={cx - pw / 2} y={cy - ph} width={pw} height={ph * 2} rx={rr} transform={`rotate(0, ${cx}, ${cy})`} />
            <rect x={cx - pw / 2} y={cy - ph} width={pw} height={ph * 2} rx={rr} transform={`rotate(60, ${cx}, ${cy})`} />
            <rect x={cx - pw / 2} y={cy - ph} width={pw} height={ph * 2} rx={rr} transform={`rotate(120, ${cx}, ${cy})`} />
          </clipPath>
          <clipPath id="flower-clip-inner">
            <rect x={cx - (pw - 14) / 2} y={cy - (ph - 7)} width={pw - 14} height={(ph - 7) * 2} rx={rr - 5} transform={`rotate(0, ${cx}, ${cy})`} />
            <rect x={cx - (pw - 14) / 2} y={cy - (ph - 7)} width={pw - 14} height={(ph - 7) * 2} rx={rr - 5} transform={`rotate(60, ${cx}, ${cy})`} />
            <rect x={cx - (pw - 14) / 2} y={cy - (ph - 7)} width={pw - 14} height={(ph - 7) * 2} rx={rr - 5} transform={`rotate(120, ${cx}, ${cy})`} />
          </clipPath>
        </defs>
        {/* Outer flower shape — border */}
        <g clipPath="url(#flower-clip)">
          <rect x="0" y="0" width="200" height="200" fill="rgba(255,255,255,0.3)" />
        </g>
        {/* Photo clipped into inner flower shape */}
        <image
          href={care3}
          x="10" y="10" width="180" height="180"
          clipPath="url(#flower-clip-inner)"
          preserveAspectRatio="xMidYMid slice"
        />
      </svg>
    </motion.div>
  );
};

const Figure8Blob: React.FC = () => {
  const w = 140;
  const h = 148;
  // Parameterized — center figure-8 inside the viewBox so neither side gets clipped
  const cx = w / 2;
  const cy1 = h * 0.31;
  const cy2 = h * 0.685;
  const rx1 = w * 0.4;
  const ry1 = h * 0.295;
  const rx2 = w * 0.435;
  const ry2 = h * 0.315;
  const innerScale = 0.9;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.7 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.7 }}
      style={{
        position: 'absolute',
        width: 'clamp(95px, 11vw, 140px)',
        height: 'clamp(100px, 11.5vw, 148px)',
        ...blobPositions[3],
        zIndex: 0,
        pointerEvents: 'none',
        transform: 'rotate(3deg)',
      }}
    >
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="100%">
        <defs>
          <clipPath id="fig8-clip">
            <ellipse cx={cx} cy={cy1} rx={rx1} ry={ry1} />
            <ellipse cx={cx} cy={cy2} rx={rx2} ry={ry2} />
          </clipPath>
          <clipPath id="fig8-clip-inner">
            <ellipse cx={cx} cy={cy1} rx={rx1 * innerScale} ry={ry1 * innerScale} />
            <ellipse cx={cx} cy={cy2} rx={rx2 * innerScale} ry={ry2 * innerScale} />
          </clipPath>
        </defs>
        <g clipPath="url(#fig8-clip)">
          <rect x="0" y="0" width={w} height={h} fill="rgba(255,255,255,0.3)" />
        </g>
        <image
          href={care4}
          x="0" y="0" width={w} height={h}
          clipPath="url(#fig8-clip-inner)"
          preserveAspectRatio="xMidYMid slice"
        />
      </svg>
    </motion.div>
  );
};

// Photo clip path — adds wavy undulation along the bottom-right corner.
const featuredBlobPhoto =
  'M434.5,52.3C467.8,98.2,489,163.5,489,228.1C489,292.7,467.8,356.6,424.1,399.3C395,422,370,440,335,445C300,450,275,471,249.8,468.5C185.4,473.5,122.8,462,74.3,427.7C25.8,393.4,-8.6,336.3,1.2,280.4C11,224.5,65,169.8,97.4,121.8C129.8,73.8,140.6,32.5,175.8,12.5C211,-.5,270.6,8,321.6,22.5C372.6,37,401.2,6.4,434.5,52.3Z';

const FeaturedBlob: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.4 }}
      style={{
        position: 'absolute',
        bottom: '22%',
        left: '14%',
        width: 'clamp(126px, 15vw, 184px)',
        aspectRatio: '489 / 471',
        perspective: '1200px',
        zIndex: 1,
      }}
    >
      {/* Photo clipped with the wavy blob shape — keep natural aspect ratio */}
      <svg
        viewBox="0 0 489 471"
        width="100%"
        height="100%"
        role="img"
        aria-label="Parent and child learning together"
        style={{ position: 'relative' }}
      >
        <defs>
          <clipPath id="featured-blob-clip">
            <path d={featuredBlobPhoto} />
          </clipPath>
          <clipPath id="featured-blob-clip-inner">
            <path d={featuredBlobPhoto} transform="translate(20, 19) scale(0.92)" />
          </clipPath>
        </defs>
        {/* Semi-transparent white ring — matches the outline effect on the other three blobs */}
        <g clipPath="url(#featured-blob-clip)">
          <rect x="0" y="0" width="489" height="471" fill="rgba(255,255,255,0.3)" />
        </g>
        <image
          href={care2}
          x="0" y="0" width="489" height="471"
          clipPath="url(#featured-blob-clip-inner)"
          preserveAspectRatio="xMidYMid slice"
        />
      </svg>

    </motion.div>
  );
};

// Parse `**bold**` markers from i18n strings into <strong> elements
const renderRich = (text: string): React.ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} style={{ color: '#1a1a2e' }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });

const ChatHeader: React.FC<{ scale?: number }> = ({ scale = 1 }) => {
  const { t } = useTranslation();
  const f = (n: number) => n * scale;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${f(18)}px ${f(12)}px ${f(10)}px`, borderBottom: '1px solid rgba(20, 18, 80, 0.08)' }}>
      <svg width={f(12)} height={f(12)} viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M9 2L4 7l5 5" stroke="#1a1a2e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div style={{ display: 'flex', alignItems: 'center', gap: f(5) }}>
        <img
          src={yagooAvatar}
          alt=""
          aria-hidden="true"
          style={{ width: f(20), height: f(20), display: 'block', objectFit: 'contain' }}
        />
        <span style={{ fontSize: f(12), fontWeight: 600, color: '#1a1a2e', letterSpacing: 0.2 }}>
          {t('hero.mockHeaderName')}
        </span>
      </div>
      <svg width={f(12)} height={f(12)} viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M2 2l10 10M12 2L2 12" stroke="#1a1a2e" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </div>
  );
};

const InputBar: React.FC<{ scale?: number }> = ({ scale = 1 }) => {
  const { t } = useTranslation();
  const f = (n: number) => n * scale;
  return (
    <div style={{ padding: `${f(6)}px ${f(10)}px ${f(10)}px`, display: 'flex', alignItems: 'center', gap: f(7), background: '#fff', borderTop: '1px solid #f2f2f7' }}>
      <div style={{ width: f(22), height: f(22), borderRadius: f(6), background: '#eef0fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={f(11)} height={f(11)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2" stroke="#7c6bfd" strokeWidth="1.8" />
          <circle cx="9" cy="10" r="1.5" fill="#7c6bfd" />
          <path d="M21 16l-5-5-9 9" stroke="#7c6bfd" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ flex: 1, height: f(24), borderRadius: f(12), background: '#f5f5f9', display: 'flex', alignItems: 'center', padding: `0 ${f(10)}px`, fontSize: f(10), color: '#a5a5b8' }}>
        {t('hero.mockInputPlaceholder')}
      </div>
      <div style={{ width: f(24), height: f(24), borderRadius: '50%', background: '#eef0fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={f(11)} height={f(11)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 12l18-9-7 19-3-8-8-2z" stroke="#7c6bfd" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
};

const DualDeviceShowcase: React.FC = () => {
  const { t } = useTranslation();
  return (
    <motion.div
      className="dual-showcase-frame"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      <div className="dual-showcase-content">
      {/* iPad — landscape, back-right */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 580,
          height: 440,
          borderRadius: 22,
          background: '#1a1a2e',
          padding: 6,
          boxShadow: '0 40px 80px -20px rgba(20, 18, 80, 0.4), 0 14px 24px -8px rgba(20, 18, 80, 0.2)',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 17,
            background: 'linear-gradient(180deg, #dbeae6 0%, #e6dff3 22%, #f4f1fa 60%, #ffffff 100%)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <ChatHeader scale={1.05} />

          {/* Conversation */}
          <div style={{ flex: 1, overflow: 'hidden', padding: '4px 18px 8px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={{ textAlign: 'center', fontSize: 9.5, color: '#9b9bb1' }}>{t('hero.mock2_time')}</div>

            {/* User bubble */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ maxWidth: '60%', background: '#7c6bfd', color: '#fff', padding: '7px 12px', borderRadius: 12, fontSize: 10, lineHeight: 1.5 }}>
                {t('hero.mock2_user')}
              </div>
            </div>

            {/* AI card */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '10px 12px', fontSize: 9, lineHeight: 1.65, color: '#3f3f55', boxShadow: '0 2px 6px rgba(20,18,80,0.04)' }}>
              <p style={{ margin: '0 0 5px', color: '#1a1a2e' }}>{t('hero.mock2_lead')}</p>
              <p style={{ margin: '0 0 6px' }}>{renderRich(t('hero.mock2_p1'))}</p>
              <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#1a1a2e' }}>{t('hero.mock2_h1')}</p>
              <p style={{ margin: '0 0 6px' }}>{renderRich(t('hero.mock2_p2'))}</p>
              <p style={{ margin: '0 0 6px' }}>{renderRich(t('hero.mock2_p3'))}</p>
              <p style={{ margin: 0 }}>{t('hero.mock2_p4')}</p>
            </div>

            {/* Chips */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 2 }}>
              {[t('hero.mock2_chip1'), t('hero.mock2_chip2'), t('hero.mock2_chip3')].map((label, i) => (
                <div
                  key={i}
                  style={{
                    background: '#fff',
                    borderRadius: 999,
                    padding: '6px 12px',
                    fontSize: 9,
                    color: '#3f3f55',
                    textAlign: 'center',
                    boxShadow: '0 1px 3px rgba(20,18,80,0.04)',
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          <InputBar scale={1.05} />
        </div>
      </div>

      {/* iPhone — portrait, front-left */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 100,
          width: 195,
          height: 420,
          borderRadius: 28,
          background: '#1a1a2e',
          padding: 6,
          boxShadow: '0 40px 80px -20px rgba(20, 18, 80, 0.45), 0 14px 24px -8px rgba(20, 18, 80, 0.22)',
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            borderRadius: 23,
            background: 'linear-gradient(180deg, #dbeae6 0%, #e6dff3 24%, #f4f1fa 55%, #ffffff 90%)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <ChatHeader scale={0.85} />

          {/* Conversation */}
          <div style={{ flex: 1, overflow: 'hidden', padding: '2px 10px 6px', display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ textAlign: 'center', fontSize: 7.5, color: '#9b9bb1' }}>{t('hero.mock1_date')}</div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ maxWidth: '85%', background: '#7c6bfd', color: '#fff', padding: '6px 9px', borderRadius: 10, fontSize: 7.5, lineHeight: 1.5 }}>
                {t('hero.mock1_user')}
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: 9, padding: '8px 9px', fontSize: 7, lineHeight: 1.6, color: '#3f3f55', boxShadow: '0 2px 6px rgba(20,18,80,0.04)' }}>
              <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: 7.5, marginBottom: 3 }}>{t('hero.mock1_h1')}</div>
              <p style={{ margin: '0 0 4px' }}>{renderRich(t('hero.mock1_p1'))}</p>
              <p style={{ margin: '0 0 5px' }}>{renderRich(t('hero.mock1_p2'))}</p>
              <div style={{ fontWeight: 700, color: '#1a1a2e', fontSize: 7.5, marginBottom: 3 }}>{t('hero.mock1_h2')}</div>
              <p style={{ margin: 0 }}>{renderRich(t('hero.mock1_p3'))}</p>
            </div>

            {/* Suggested follow-up chips — match iPad style, scaled down */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 2 }}>
              {[t('hero.mock1_chip1'), t('hero.mock1_chip2')].map((label, i) => (
                <div
                  key={i}
                  style={{
                    background: '#fff',
                    borderRadius: 999,
                    padding: '5px 10px',
                    fontSize: 7.5,
                    color: '#3f3f55',
                    textAlign: 'center',
                    boxShadow: '0 1px 3px rgba(20,18,80,0.04)',
                  }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          <InputBar scale={0.78} />
        </div>
      </div>
      </div>
    </motion.div>
  );
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const fade = (delay = 0, y = 20) =>
    reduce
      ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.25, delay } }
      : { initial: { opacity: 0, y }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay } };

  return (
    <div className="main-wrapper">
      {/* Hero Section */}
      <section
        aria-labelledby="home-hero-title"
        style={{
          backgroundColor: 'rgb(79, 103, 255)',
          marginTop: '-110px',
          padding: 'clamp(96px, 12vw, 160px) 0 clamp(20px, 3vw, 40px) 0',
          position: 'relative',
          overflow: 'hidden',
          borderBottomLeftRadius: '40px',
          borderBottomRightRadius: '40px',
        }}
      >
        {/* Background image — shifted down so its central curve sits near the middle blob row */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '160px',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${heroBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
        />

        {/* Contrast overlay — guarantees ≥4.5:1 for white text on any bg */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(20,18,80,0.25) 0%, rgba(20,18,80,0.05) 45%, rgba(20,18,80,0.18) 100%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <div aria-hidden="true" className="hero-decor-blobs">
          <CloverBlob />
          <FlowerBlob />
          <Figure8Blob />
          <FeaturedBlob />
        </div>

        <div className="padding-global" style={{ position: 'relative', zIndex: 2 }}>
          <div
            className="container-large"
            style={{
              textAlign: 'center',
              maxWidth: '880px',
              marginInline: 'auto',
            }}
          >
            {/* Eyebrow / badge */}
            {/* Title */}
            <motion.h1
              {...fade(0.08, 24)}
              id="home-hero-title"
              className="heading-style-h1"
              style={{
                textAlign: 'center',
                margin: '0 auto 20px',
                color: '#ffffff',
                lineHeight: 1.15,
                whiteSpace: 'pre-line',
              }}
            >
              {t('hero.title')}
            </motion.h1>

            {/* Subtitle — softened weight/opacity so H1 holds the anchor */}
            <motion.p
              {...fade(0.16)}
              style={{
                fontSize: 'clamp(14px, 1.05vw, 16px)',
                lineHeight: 1.65,
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 400,
                margin: '0 auto 36px',
                maxWidth: '52ch',
                letterSpacing: '0.01em',
              }}
            >
              {t('hero.description')}
            </motion.p>

            <div style={{ marginBottom: '72px' }} />

            <motion.div {...fade(0.24)} style={{ marginBottom: '24px' }}>
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  color: 'rgba(255,255,255,0.92)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '12px',
                  textTransform: 'uppercase',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{ width: '28px', height: '1px', background: 'rgba(255,255,255,0.55)' }}
                />
                {t('hero.productBadge')}
                <span
                  aria-hidden="true"
                  style={{ width: '28px', height: '1px', background: 'rgba(255,255,255,0.55)' }}
                />
              </span>
            </motion.div>

            {/* Product name + tagline merged into a single line — reduces hierarchy collision with H1 */}
            <motion.h2
              id="product-showcase-title"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{
                fontFamily: "Nunito, 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
                fontSize: 'clamp(16px, 1.4vw, 20px)',
                fontWeight: 500,
                color: '#ffffff',
                margin: '0 0 36px',
                lineHeight: 1.4,
              }}
            >
              <span style={{ fontWeight: 700 }}>{t('hero.productTitle')}</span>
              <span style={{ color: 'rgba(255,255,255,0.7)', margin: '0 10px' }}>·</span>
              <span style={{ color: 'rgba(255,255,255,0.78)', fontWeight: 400 }}>
                {t('hero.productLine')}
              </span>
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ scale: 1.04 }}
              style={{ marginBottom: 56, display: 'inline-block' }}
            >
              <Link
                to="/sproutly"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: 'clamp(14px, 1.4vw, 18px) clamp(28px, 3vw, 44px)',
                  borderRadius: 999,
                  background: '#ffffff',
                  color: 'rgb(79, 103, 255)',
                  fontFamily: "Nunito, 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
                  fontSize: 'clamp(15px, 1.3vw, 17px)',
                  fontWeight: 700,
                  textDecoration: 'none',
                  boxShadow: '0 16px 36px -8px rgba(20, 18, 80, 0.4), 0 4px 10px -4px rgba(20, 18, 80, 0.2)',
                  transition: 'box-shadow 0.2s ease',
                }}
              >
                {t('hero.ctaTryNow')}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </motion.div>

            <DualDeviceShowcase />

          </div>
        </div>
      </section>

      {/* Blog Section - reuse existing */}
      <Blog />
    </div>
  );
};

export default Home;
